// src/app/data/chat/api/ChatApi.ts
import { DataFetcher } from '@/app/data/api/DataFetcher';
import type {
  ConversationResponseDTO,
  AIModel,
  ConversationMessagesPageDTO,
  ConversationMessageResponseDTO,
} from '@/app/core/chatPage/chatCopmonents/shared/constants';
import { BASIC_CONSTANTS_NETWORK } from '@/app/network/models/NetworkConstants';
import { getInitData } from '@/app/network/utils/TelegramAuthUtil';

interface ProviderSummary {
  id: string;
  displayName: string;
  capabilities: string[];
}

interface CapabilityCatalog {
  channel: string;
  defaultModel: string;
  models: Array<{
    id: string;
    label: string;
    description?: string;
  }>;
}

export class ChatApi {
  private fetcher = new DataFetcher();

  async getConversations(): Promise<ConversationResponseDTO[]> {
  const data = await this.fetcher.get<any>('/client/conversations');
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.items)) {
    return data.items;
  }
  console.warn('[ChatApi] Неожиданный формат ответа getConversations:', data);
  return [];
}

async getConversationMessages(conversationId: number, page = 0, size = 50): Promise<ConversationMessagesPageDTO> {
  return this.fetcher.get<ConversationMessagesPageDTO>(
    `/client/conversations/${conversationId}/messages`,
    { page, size }
  );
}

async getConversationMessage(conversationId: number, messageId: number): Promise<ConversationMessageResponseDTO> {
  return this.fetcher.get<ConversationMessageResponseDTO>(
    `/client/conversations/${conversationId}/messages/${messageId}`
  );
}

  async createConversation(title: string, provider: string): Promise<ConversationResponseDTO> {
    const baseUrl = BASIC_CONSTANTS_NETWORK.BASE_URL;
    const url = `${baseUrl}/client/conversations`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    const stored = localStorage.getItem('auth_tokens');
    if (stored) {
      try {
        const tokens = JSON.parse(stored);
        if (tokens.accessToken) {
          headers['Authorization'] = `Bearer ${tokens.accessToken}`;
        }
      } catch {}
    }

    const initData = getInitData();
    if (initData) {
      headers['X-Telegram-Init-Data'] = initData;
    }

    const body = new URLSearchParams({
      title,
      provider,
      conversationType: 'CHAT',
    });

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: body.toString(),
      credentials: 'include',
    });

    const responseData = await response.json();
    if (!response.ok) {
      console.error('[ChatApi] createConversation error:', responseData);
      throw new Error(responseData.message || 'Ошибка создания диалога');
    }
    return responseData.data as ConversationResponseDTO;
  }

  async deleteConversation(conversationId: number): Promise<void> {
    await this.fetcher.delete<void>(`/client/conversations/${conversationId}`);
  }

  async updateConversationTitle(conversationId: number, title: string): Promise<ConversationResponseDTO> {
    return this.fetcher.patch<ConversationResponseDTO>(
      `/client/conversations/${conversationId}/title`,
      { title }
    );
  }

  async getAvailableModels(): Promise<AIModel[]> {
    const providers = await this.fetcher.get<ProviderSummary[]>('/client/llm/providers');
    console.log('[ChatApi] providers:', providers);

    const modelPromises: Promise<AIModel[]>[] = [];

    for (const provider of providers) {
      const providerId = provider.id;
      const providerName = provider.displayName || provider.id;
      const channels = provider.capabilities || [];

      for (const channel of channels) {
        modelPromises.push(
          this.fetcher
            .get<CapabilityCatalog>(`/client/llm/providers/${providerId}/capabilities/${channel}`)
            .then((catalog) => {
              const actualChannel = catalog.channel || channel;
              return (catalog.models || []).map((model) => ({
                id: model.id,
                name: model.label || model.id,
                description: model.description || '',
                provider: providerName,
                channel: actualChannel as 'chat' | 'image' | 'video',
                contextWindowTokens: (model as any).contextWindowTokens,
              }));
            })
            .catch((err) => {
              console.warn(`[ChatApi] Ошибка загрузки ${providerId}/${channel}`, err);
              return [];
            })
        );
      }
    }

    const modelsArrays = await Promise.all(modelPromises);
    const allModels = modelsArrays.flat();
    const uniqueModels = Array.from(new Map(allModels.map((m) => [m.id, m])).values());
    console.log('[ChatApi] Models loaded:', uniqueModels);
    return uniqueModels;
  }
}